import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * ScrollContext — ページ内の独立スクロール領域を Header に登録する仕組み
 *
 * 課題: ProjectDetailPage 等は body 非スクロール (overflow:hidden) で、
 *       中央カラム内の Box が overflow:auto で独立スクロール。
 *       Header の HideOnScroll は window scroll に反応するため、こうしたページでは
 *       永遠に発火しない → スクロール下方でも Header が常に表示 → AIWritingToolbar 等の
 *       sticky が Header に隠れる。
 *
 * 解法: ページが「自分のメインスクロール要素」をこの context に登録する。
 *       Header の useScrollTrigger が target としてその要素を使う。
 *
 * 使い方:
 *   - ProjectDetailPage:
 *       const { setScrollEl } = useScrollContext();
 *       const ref = useRef(null);
 *       useEffect(() => { setScrollEl(ref.current); return () => setScrollEl(null); }, [setScrollEl]);
 *       <Box ref={ref} sx={{ overflow:'auto' }}>...</Box>
 *
 *   - Header (内部):
 *       const { scrollEl } = useScrollContext();
 *       const trigger = useScrollTrigger({ target: scrollEl || undefined, threshold: 40 });
 */
const ScrollContext = createContext({
  scrollEl: null,
  setScrollEl: () => {},
});

export const ScrollProvider = ({ children }) => {
  const [scrollEl, setScrollElState] = useState(null);
  const setScrollEl = useCallback((el) => {
    setScrollElState(el || null);
  }, []);
  return (
    <ScrollContext.Provider value={{ scrollEl, setScrollEl }}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScrollContext = () => useContext(ScrollContext);

export default ScrollContext;
